import { arrayBuilderPages } from 'platform/forms-system/src/js/patterns/array-builder';
import {
  arrayBuilderYesNoSchema,
  arrayBuilderYesNoUI,
  descriptionUI,
  selectSchema,
  selectUI,
  titleUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import FileUploadDescription from '../../components/FormDescriptions/FileUploadDescription';
import { attachmentSchema, attachmentUI, blankSchema } from '../../definitions';
import content from '../../locales/en/content.json';

const defaultYesNoOptions = {
  title: content['documents--yes-no-title'],
  hint: null,
  labelHeaderLevel: '',
};

const defaultYesNoOptionsMore = {
  title: content['documents--yes-no-more-title'],
  hint: null,
  labelHeaderLevel: '',
};

const getPaths = path => ({
  overviewPath: `${path}-overview`,
  summaryPath: path,
  uploadPath: `${path}-upload/:index`,
});

const getSummaryKey = arrayPath => `view:${arrayPath}HasDocuments`;

const createDocumentOptions = ({
  arrayPath,
  summaryTitle,
  arrayBuilderSummaryTitle = summaryTitle,
  required = false,
  summaryDescription,
  nounSingular = content['noun--document-singular'],
  nounPlural = content['noun--document-plural'],
}) => ({
  arrayPath,
  nounSingular,
  nounPlural,
  required,
  text: {
    summaryTitle: arrayBuilderSummaryTitle,
    summaryTitleWithoutItems: arrayBuilderSummaryTitle,
    ...(required === false && summaryDescription
      ? { summaryDescriptionWithoutItems: summaryDescription }
      : {}),
    getItemName: item => item?.attachmentFile?.name || nounSingular,
  },
});

const createDocumentOverviewPage = ({ summaryTitle, summaryDescription }) => ({
  uiSchema: {
    ...titleUI(summaryTitle),
    ...descriptionUI(summaryDescription),
  },
  schema: blankSchema,
});

const createDocumentSummaryPage = ({
  options,
  summaryKey,
  yesNoOptions,
  yesNoOptionsMore,
}) => ({
  uiSchema: {
    [summaryKey]: arrayBuilderYesNoUI(
      options,
      {
        ...defaultYesNoOptions,
        ...yesNoOptions,
      },
      {
        ...defaultYesNoOptionsMore,
        ...yesNoOptionsMore,
      },
    ),
  },
  schema: {
    type: 'object',
    required: [summaryKey],
    properties: {
      [summaryKey]: arrayBuilderYesNoSchema,
    },
  },
});

const createDocumentUploadPage = ({
  uploadTitle,
  uploadTitleAddOn,
  uploadLabel = uploadTitle,
  uploadDescription = FileUploadDescription,
  attachmentId,
}) => {
  const hasAttachmentIdOptions =
    attachmentId && typeof attachmentId === 'object';
  return {
    uiSchema: {
      ...titleUI(uploadTitle, uploadTitleAddOn),
      ...descriptionUI(uploadDescription),
      attachmentFile: attachmentUI({
        title: uploadLabel,
        ...(hasAttachmentIdOptions ? {} : { attachmentId }),
      }),
      ...(hasAttachmentIdOptions && {
        attachmentId: selectUI({
          title: attachmentId.title,
          labels: Object.fromEntries(
            attachmentId.labels.map(key => [key, key]),
          ),
        }),
      }),
    },
    schema: {
      type: 'object',
      required: [
        'attachmentFile',
        ...(hasAttachmentIdOptions ? ['attachmentId'] : []),
      ],
      properties: {
        attachmentFile: attachmentSchema,
        ...(hasAttachmentIdOptions && {
          attachmentId: selectSchema(attachmentId.labels),
        }),
      },
    },
  };
};

/**
 * Creates a standardized set of ArrayBuilder pages for document upload.
 *
 * Generates overview, summary, and upload pages for document-based workflows
 * using the VA forms-system ArrayBuilder pattern. Supports both required and
 * optional flows, dynamic titles derived from form data, configurable yes/no
 * prompts, and single or selectable attachment types.
 *
 * Required flows generate:
 * - overview page
 * - summary page
 * - upload page
 *
 * Optional flows generate:
 * - summary page
 * - upload page
 *
 * Upload pages support either:
 * - a fixed attachmentId string
 * - a selectable attachmentId object with labels
 *
 * @param {Object} config - Page configuration options
 * @param {string} config.arrayPath - ArrayBuilder array path and page key prefix
 * @param {string} config.pagePath - Base route path used to derive overview and upload routes
 * @param {string|Function} config.summaryTitle - Summary page title or title component
 * @param {string} config.uploadTitle - Upload page title
 * @param {string} [config.uploadLabel=config.uploadTitle] - Upload field label
 * @param {React.Component} config.summaryDescription - Description component for overview/summary pages
 * @param {React.Component} [config.uploadDescription=FileUploadDescription] - Upload page description component
 * @param {string|Object} config.attachmentId - Attachment ID string or selectable attachment config
 * @param {Function} [config.depends] - Optional depends function for conditional pages
 * @param {boolean} [config.required=false] - Whether the document flow is required
 * @param {Object} [config.yesNoOptions] - Overrides for the initial yes/no prompt
 * @param {Object} [config.yesNoOptionsMore] - Overrides for the additional-item yes/no prompt
 * @returns {Object} ArrayBuilder page configuration object
 */
export const createDocumentPages = config => {
  const options = createDocumentOptions(config);
  const paths = getPaths(config.pagePath);
  const summaryKey = getSummaryKey(config.arrayPath);
  return arrayBuilderPages(options, pageBuilder => ({
    ...(options.required !== false && {
      [`${config.arrayPath}Overview`]: pageBuilder.introPage({
        title: config.summaryTitle,
        path: paths.overviewPath,
        depends: config.depends,
        ...createDocumentOverviewPage(config),
      }),
    }),

    [`${config.arrayPath}Summary`]: pageBuilder.summaryPage({
      title: config.summaryTitle,
      path: paths.summaryPath,
      depends: config.depends,
      ...createDocumentSummaryPage({
        options,
        summaryKey,
        yesNoOptions: config.yesNoOptions,
        yesNoOptionsMore: config.yesNoOptionsMore,
      }),
    }),

    [`${config.arrayPath}Upload`]: pageBuilder.itemPage({
      title: config.uploadTitle,
      path: paths.uploadPath,
      depends: config.depends,
      ...createDocumentUploadPage(config),
    }),
  }));
};
